import React, {useEffect, useRef, useState} from "react";
import {Table, Input, message, Tag, Button, Space, Form, Card, Tooltip} from "antd";
import SubCompanyInput from "../../../../components/createDeliveryRequest/deliveryRequestForm/pickupInformation/subCompanyInput/SubCompanyInput";
import CompanyInput from "../../../../components/createDeliveryRequest/deliveryRequestForm/pickupInformation/companyInput/CompanyInput";
import {useStore} from "../../../../hooks/useStore";

export const PoolFormCompanyTable = ({pool, formValues, setFormValues, subCompanyOptions = []}) => {
    const stores = useStore();
    const [companies, setCompanies] = useState(pool?.companies || []);
    const [subCompanies, setSubCompanies] = useState(pool?.subCompanies || []);
    const [searchText, setSearchText] = useState("");
    const [sortedInfo, setSortedInfo] = useState({
        columnKey: "companyName",
        order: "ascend"
    });
    const [editingRow, setEditingRow] = useState(null);
    const [selectedSubCompanies, setSelectedSubCompanies] = useState([]);
    const [selectedSubCompaniesObject, setSelectedSubCompaniesObject] = useState([]);
    const [editingCompany, setEditingCompany] = useState(null);

    // Yeni firma ekleme için state'ler
    const [showAddForm, setShowAddForm] = useState(false);
    const [newCompany, setNewCompany] = useState(null);
    const [newSubCompanies, setNewSubCompanies] = useState([]);
    const [newSubCompaniesObject, setNewSubCompaniesObject] = useState([]);
    const oldCity = useRef(formValues?.city);

    useEffect(() => {
        setFormValues({
            ...formValues,
            companies,
            subCompanies
        });
    }, [companies, subCompanies]);

    useEffect(() => {
        if (formValues?.city !== oldCity.current) {
            setCompanies([]);
            setSubCompanies([]);
            oldCity.current = formValues?.city;
        }
    }, [formValues?.city]);

    const handleChange = (pagination, filters, sorter) => {
        setSortedInfo(sorter);
    };

    const dataSource = companies
        .filter(company => {
            const companyInfo = formValues.companyInfoArray.find(
                info => info.company === company._id
            );
            return companyInfo?.subCompanies.length > 0;
        })
        .map(company => ({
            ...company,
            key: company._id,
            subCompanies: subCompanies.filter(subCompany => {
                const companyInfo = formValues.companyInfoArray.find(
                    info => info.company === company._id
                );
                return companyInfo?.subCompanies?.includes(subCompany._id);
            })
        }));

    const turkishToLowerCase = text => {
        return text
            .replace(/İ/g, "i")
            .replace(/I/g, "ı")
            .toLowerCase();
    };

    const filteredData = dataSource.filter(
        item =>
            turkishToLowerCase(item.companyName).includes(turkishToLowerCase(searchText)) ||
            item.subCompanies.some(subCompany =>
                turkishToLowerCase(subCompany.name).includes(turkishToLowerCase(searchText))
            )
    );

    const turkishCollator = new Intl.Collator("tr", {sensitivity: "base"});

    const handleEdit = record => {
        setEditingRow(record.key);
        setEditingCompany(record._id);
        setSelectedSubCompanies(record.subCompanies.map(sc => sc._id));
    };

    const handleSave = () => {
        if (selectedSubCompanies.length === 0) {
            message.error("Lütfen en az bir alt firma seçiniz.");
            return;
        }
        console.log(selectedSubCompanies);
        // Seçilen alt şirketleri formValues'a ekle
        if (editingCompany && formValues && setFormValues) {
            // Mevcut formValues'ı kopyala
            const updatedFormValues = {...formValues};

            // companyInfoArray içinde editingCompany'ye ait kaydı bul
            const companyIndex = updatedFormValues.companyInfoArray.findIndex(
                info => info.company === editingCompany
            );

            if (companyIndex !== -1) {
                // Eğer bu şirket zaten varsa, alt şirketlerini güncelle
                updatedFormValues.companyInfoArray[
                    companyIndex
                ].subCompanies = selectedSubCompanies;
            } else {
                // Eğer bu şirket henüz yoksa, yeni bir kayıt ekle
                updatedFormValues.companyInfoArray.push({
                    company: editingCompany,
                    subCompanies: selectedSubCompanies
                });
            }

            setSubCompanies(prev => {
                const allSubCompanies = [...prev];

                const remainingSubCompanies = allSubCompanies.filter(subCompany => {
                    const isUsedByOtherCompanies = formValues.companyInfoArray.some(info => {
                        if (info.company === editingCompany) return false;
                        return info.subCompanies.includes(subCompany._id);
                    });

                    return selectedSubCompanies.includes(subCompany._id) || isUsedByOtherCompanies;
                });

                const newSubCompanies = selectedSubCompaniesObject.filter(
                    sc => !remainingSubCompanies.some(existing => existing._id === sc._id)
                );

                const updatedSubCompanies = [...remainingSubCompanies, ...newSubCompanies];
                return updatedSubCompanies;
            });

            setFormValues(updatedFormValues);
        }

        // Düzenleme modunu kapat
        setEditingRow(null);
        setEditingCompany(null);
    };

    const handleDelete = record => {
        // Silinen şirketi formValues'dan kaldır
        if (record && formValues && setFormValues) {
            // Mevcut formValues'ı kopyala
            const updatedFormValues = {...formValues};

            // companyInfoArray içinden silinecek şirketi bul ve kaldır
            updatedFormValues.companyInfoArray = updatedFormValues.companyInfoArray.filter(
                info => info.company !== record._id
            );

            // formValues'ı güncelle
            setFormValues(updatedFormValues);

            // Companies state'inden kaldır
            setCompanies(prevCompanies =>
                prevCompanies.filter(company => company._id !== record._id)
            );

            // SubCompanies state'inden, silinen şirkete ait alt şirketleri kaldır
            setSubCompanies(prevSubCompanies => {
                // Diğer şirketlerin alt şirketlerini bul
                const remainingSubCompanyIds = updatedFormValues.companyInfoArray.flatMap(
                    info => info.subCompanies
                );

                // Sadece diğer şirketlere ait alt şirketleri tut
                return prevSubCompanies.filter(subCompany =>
                    remainingSubCompanyIds.includes(subCompany._id)
                );
            });
        }
    };

    const columns = [
        {
            title: "Firma",
            dataIndex: "companyName",
            key: "companyName",
            sorter: (a, b) => turkishCollator.compare(a.companyName, b.companyName),
            sortOrder: sortedInfo.columnKey === "companyName" && sortedInfo.order,
            defaultSortOrder: "ascend",
            width: 250
        },
        {
            title: "Alt Firmalar",
            dataIndex: "subCompanies",
            key: "subCompanies",
            render: (subCompanies, record) => {
                if (editingRow === record.key) {
                    return (
                        <SubCompanyInput
                            company={editingCompany}
                            value={selectedSubCompanies}
                            onChange={value => setSelectedSubCompanies(value)}
                            onSelectedSubCompaniesChange={value =>
                                setSelectedSubCompaniesObject(value)
                            }
                            city={formValues.city}
                            mode="multiple"
                            disabled={false}
                            initialCall={true}
                            style={{width: "100%"}}
                        />
                    );
                }
                return (
                    <>
                        {subCompanies.map(subCompany => (
                            <Tag key={subCompany._id}>{subCompany.name}</Tag>
                        ))}
                    </>
                );
            }
        },
        {
            title: "İşlemler",
            key: "actions",
            render: (_, record) => {
                // Havuzda kaç firma olduğunu kontrol et
                const isLastCompany = dataSource.length <= 1;

                return (
                    <Space size="small">
                        {editingRow === record.key ? (
                            <Button type="primary" size="small" onClick={handleSave}>
                                Kaydet
                            </Button>
                        ) : (
                            <Button type="primary" size="small" onClick={() => handleEdit(record)}>
                                Düzenle
                            </Button>
                        )}
                        <Tooltip
                            title={isLastCompany ? "Havuzda en az bir firma bulunmalıdır" : ""}
                        >
                            <Button
                                type="primary"
                                danger
                                size="small"
                                onClick={() => handleDelete(record)}
                                disabled={isLastCompany}
                            >
                                Sil
                            </Button>
                        </Tooltip>
                    </Space>
                );
            }
        }
    ];

    // Yeni firma ekleme formunu gösterme/gizleme fonksiyonu
    const toggleAddForm = () => {
        setShowAddForm(!showAddForm);
        if (!showAddForm) {
            // Form açılırken state'i temizle
            setNewCompany(null);
            setNewSubCompanies([]);
            setNewSubCompaniesObject([]);
        }
    };

    // Yeni firma ekleme fonksiyonu
    const handleAddCompany = () => {
        if (!newCompany) {
            message.error("Lütfen bir firma seçiniz.");
            return;
        }

        if (newSubCompanies.length === 0) {
            message.error("Lütfen en az bir alt firma seçiniz.");
            return;
        }

        // Seçilen firma zaten eklenmiş mi kontrol et
        const isCompanyAlreadyAdded = formValues.companyInfoArray.some(
            info => info.company === newCompany
        );

        if (isCompanyAlreadyAdded) {
            message.error("Bu firma zaten eklenmiş.");
            return;
        }

        // Yeni firmayı formValues'a ekle
        const updatedFormValues = {...formValues};
        updatedFormValues.companyInfoArray.push({
            company: newCompany,
            subCompanies: newSubCompanies
        });

        // Alt firmaları güncelle
        setSubCompanies(prev => {
            const oldSubcompanies = [...prev];
            const newSubcompaniesToAdd = newSubCompaniesObject.filter(
                sc => !oldSubcompanies.map(s => s._id).includes(sc._id)
            );
            return [...oldSubcompanies, ...newSubcompaniesToAdd];
        });
        setCompanies(prev => {
            const newCompanyObject = stores.appStore.companyListFilter.find(
                company => company._id === newCompany
            );
            if (prev.some(company => company?._id === newCompanyObject?._id)) {
                return prev;
            }
            return [...prev, newCompanyObject];
        });

        // formValues'ı güncelle
        setFormValues({
            ...updatedFormValues,
            companyInfoArray: updatedFormValues.companyInfoArray.filter(
                info => typeof info.company !== "undefined"
            )
        });

        // Formu gizle ve state'i temizle
        setShowAddForm(false);
        setNewCompany(null);
        setNewSubCompanies([]);
        setNewSubCompaniesObject([]);

        message.success("Firma eklendi.");
    };

    return (
        <>
            <div style={{display: "flex", justifyContent: "space-between", marginBottom: 16}}>
                <Input.Search
                    placeholder="Firma veya alt firma ara..."
                    onChange={e => setSearchText(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }}
                    style={{width: "calc(100% - 120px)"}}
                />
                <Button type="primary" onClick={toggleAddForm}>
                    {showAddForm ? "İptal" : "Firma Ekle"}
                </Button>
            </div>

            {/* Yeni Firma Ekleme Formu */}
            {showAddForm && (
                <Card style={{marginBottom: 16}} bodyStyle={{padding: "12px"}}>
                    <Form layout="inline" style={{display: "flex", alignItems: "center"}}>
                        <Form.Item style={{flex: 1, marginRight: 8}}>
                            <CompanyInput
                                value={newCompany}
                                onChange={value => {
                                    setNewCompany(value);
                                    setNewSubCompanies([]);
                                    setNewSubCompaniesObject([]);
                                }}
                                includeInactives={false}
                                style={{width: "100%"}}
                                optionFilter={company => {
                                    return !formValues.companyInfoArray.some(
                                        info => info.company === company._id
                                    );
                                }}
                            />
                        </Form.Item>
                        <Form.Item style={{flex: 2, marginRight: 8}}>
                            <SubCompanyInput
                                company={newCompany}
                                value={newSubCompanies}
                                onChange={value => setNewSubCompanies(value)}
                                onSelectedSubCompaniesChange={value =>
                                    setNewSubCompaniesObject(value)
                                }
                                city={formValues.city}
                                mode="multiple"
                                disabled={!newCompany}
                                initialCall={true}
                                style={{width: "100%"}}
                            />
                        </Form.Item>
                        <Form.Item>
                            <Button type="primary" onClick={handleAddCompany}>
                                Ekle
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>
            )}

            <Table
                dataSource={filteredData}
                columns={columns}
                size="small"
                onChange={handleChange}
            />
        </>
    );
};
